import { useEffect, useState } from 'react';
import { billingApi } from '@/api/billing.api';
import type { ShopPlanInfo, UsageAnalytics } from '@/types/billing.types';

interface UsagePanelProps {
  plan: ShopPlanInfo;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Percentage or an honest dash — never a fabricated 0%. */
function formatRate(value: number | null): string {
  return value === null ? '—' : `${value}%`;
}

function QuotaBar({ used, limit }: { used: number; limit: number }) {
  // An unlimited/unknown limit must not render a bar: dividing by 0 gives a
  // NaN width that silently collapses to an empty track, which reads as "you
  // have used nothing" — the opposite of the truth for a heavy user.
  if (!Number.isFinite(limit) || limit <= 0) {
    return <p className="mt-2 text-2xl font-semibold text-slate-900">{used}</p>;
  }

  const pct = Math.min(100, Math.round((used / limit) * 100));
  // Colour is a nudge, not an alarm. Amber at 80% matches the threshold the
  // backend uses to unlock extra packs, so the visual warning and the actual
  // offer appear together rather than the UI crying wolf early.
  const tone = pct >= 100 ? 'bg-rose-500' : pct >= 80 ? 'bg-amber-500' : 'bg-indigo-500';

  return (
    <>
      <p className="mt-2 text-2xl font-semibold text-slate-900">
        {used}
        <span className="text-base font-normal text-slate-400"> / {limit}</span>
      </p>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </>
  );
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white/60 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-slate-900">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

/**
 * Usage & engagement for an active subscriber.
 *
 * Only rendered for subscribers. For a merchant with no plan the honest
 * content is the pricing table, not an empty dashboard — showing zeroed
 * metrics to someone who has not subscribed makes the product look dead at
 * exactly the moment it needs to look worth buying.
 */
export function UsagePanel({ plan }: UsagePanelProps) {
  const [analytics, setAnalytics] = useState<UsageAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const data = await billingApi.getUsageAnalytics();
      if (!cancelled) {
        setAnalytics(data);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plan.shop_id]);

  const { usage } = plan;
  const funnel = analytics?.funnel;

  return (
    <div className="glass-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="ghost-chip">Usage</span>
          <h2 className="mt-3 text-2xl font-semibold text-slate-900">
            This billing period
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Resets {formatDate(usage.billing_period_reset_at)}
            {plan.plan ? ` · ${plan.plan.name} plan` : ''}
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-slate-100 bg-white/60 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Products generated
          </p>
          <QuotaBar
            used={usage.assets_generated_this_month}
            limit={usage.max_assets_per_month}
          />
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white/60 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Premium generations
          </p>
          <QuotaBar
            used={usage.premium_generations_used}
            limit={usage.premium_generations_included}
          />
        </div>
      </div>

      <h3 className="mt-8 text-sm font-semibold text-slate-700">Storefront engagement</h3>

      {loading ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />
          ))}
        </div>
      ) : !analytics ? (
        <p className="mt-3 text-sm text-slate-400">
          Engagement data is temporarily unavailable. Your plan and quota above are
          unaffected.
        </p>
      ) : analytics.no_data_yet ? (
        <p className="mt-3 text-sm text-slate-500">
          No storefront activity recorded yet. Once the AR button is live on a product
          page, views and add-to-cart events will appear here.
        </p>
      ) : (
        <>
          <div className="mt-4 grid gap-4 sm:grid-cols-4">
            <Metric label="Viewer loads" value={funnel!.viewer_loaded} />
            <Metric
              label="AR launches"
              value={funnel!.ar_launched}
              hint={`${formatRate(funnel!.ar_engagement_rate)} of viewers`}
            />
            <Metric label="Try-on sessions" value={funnel!.tryon_opened} />
            <Metric
              label="Add to cart"
              value={funnel!.add_to_cart}
              hint={`${formatRate(funnel!.ar_to_cart_rate)} of AR launches`}
            />
          </div>
          <p className="mt-3 text-xs text-slate-400">
            Counted for the current billing period, the same window as your quota above.
          </p>
        </>
      )}
    </div>
  );
}

export default UsagePanel;
